"use client";

import type { ReactNode } from "react";
import { useDelayedLoading } from "@/hooks/useDelayedLoading";

interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg";
  className?: string;
  text?: string;
  /** Gecikme (ms). Verilirse spinner sadece bu süre yükleme sürdükten sonra gösterilir. */
  delayMs?: number;
}

const ringMap = {
  sm: "w-4 h-4 border-2",
  md: "w-[22px] h-[22px] border-[2.5px]",
  lg: "w-8 h-8 border-[3px]",
};

export function LoadingSpinner({
  size = "md",
  className = "",
  text,
  delayMs,
}: LoadingSpinnerProps) {
  const show = useDelayedLoading(true, delayMs);
  if (!show) return null;

  return (
    <div
      className={`flex flex-col items-center justify-center gap-3 ${className}`}
    >
      <span
        className={`inline-block rounded-full animate-spin border-brand-glow-20 border-t-brand ${ringMap[size]}`}
        style={{ animationDuration: "0.7s" }}
      />
      {text && <p className="text-sm text-muted-foreground">{text}</p>}
    </div>
  );
}

interface PageLoaderProps {
  text?: string;
  minHeight?: string;
  /** Gecikme (ms). Verilirse loader sadece bu süre yükleme sürdükten sonra gösterilir. */
  delayMs?: number;
}

export function PageLoader({
  text = "Yükleniyor…",
  minHeight = "400px",
  delayMs,
}: PageLoaderProps) {
  const show = useDelayedLoading(true, delayMs);
  if (!show) return null;

  return (
    <div className="flex items-center justify-center" style={{ minHeight }}>
      <LoadingSpinner size="lg" text={text} />
    </div>
  );
}

/** Mount edildiğinde delayMs sonra children'ı gösterir. Özel loading UI için (tek bir skeleton bileşeni değilse). */
export function DelayedRender({
  delayMs = 300,
  children,
}: {
  delayMs?: number;
  children: ReactNode;
}) {
  const show = useDelayedLoading(true, delayMs);
  if (!show) return null;
  return <>{children}</>;
}

interface SkeletonProps {
  className?: string;
  count?: number;
}

export function Skeleton({ className = "", count = 1 }: SkeletonProps) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className={`skeleton rounded ${className}`} />
      ))}
    </>
  );
}

export function CardSkeleton({ count = 1 }: { count?: number }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="border border-border rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-4">
            <Skeleton className="w-12 h-12 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-5/6" />
        </div>
      ))}
    </>
  );
}

export function TableSkeleton({
  rows = 5,
  columns = 4,
}: {
  rows?: number;
  columns?: number;
}) {
  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex gap-4 pb-3 border-b border-border">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-4 flex-1" />
        ))}
      </div>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="flex gap-4">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} className="h-8 flex-1" />
          ))}
        </div>
      ))}
    </div>
  );
}

interface ButtonLoadingProps {
  loading: boolean;
  children: React.ReactNode;
  loadingText?: string;
}

export function ButtonLoading({
  loading,
  children,
  loadingText,
}: ButtonLoadingProps) {
  if (loading) {
    return (
      <>
        <span
          className="inline-block w-4 h-4 mr-2 rounded-full border-2 animate-spin border-[rgba(10,10,10,0.25)] border-t-primary-foreground"
          style={{ animationDuration: "0.7s" }}
        />
        {loadingText || children}
      </>
    );
  }
  return <>{children}</>;
}
